/**
 * ApplicationModal Component
 * Modal for job application submission with CV selection and cover letter
 */

import React, { useState, useEffect } from 'react';
import { 
  X, 
  FileText, 
  Upload, 
  CheckCircle, 
  AlertCircle,
  Loader,
  User,
  Mail,
  Phone,
  MapPin,
  Briefcase
} from 'lucide-react';
import { createJobApplication } from '../services/careerService';
import { getUserCVs, getUserPrimaryCV } from '../services/cvService';
import { getUserScreeningProfile } from '../services/audioService';
import { useAuth } from '../../../core/contexts/AuthContext';
import { toast } from 'react-hot-toast';

/**
 * ApplicationModal Component
 * 
 * @param {Object} job - Job data
 * @param {function} onClose - Close modal handler
 * @param {function} onSuccess - Success callback
 * @returns {JSX.Element} ApplicationModal component
 */
const ApplicationModal = ({ job, onClose, onSuccess }) => {
  const { currentUser, userProfile } = useAuth();
  
  // State management
  const [step, setStep] = useState(1); // 1: CV Selection, 2: Application Details, 3: Review
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [userCVs, setUserCVs] = useState([]);
  const [selectedCV, setSelectedCV] = useState(null);
  const [screeningProfile, setScreeningProfile] = useState(null);
  const [applicationData, setApplicationData] = useState({
    coverLetter: '',
    expectedSalary: '',
    availabilityDate: '',
    additionalInfo: ''
  });
  const [errors, setErrors] = useState({});

  // Load user data on mount
  useEffect(() => {
    const loadUserData = async () => {
      if (!currentUser) return;
      
      try {
        setLoading(true);
        
        // Load user CVs
        const cvs = await getUserCVs(currentUser.uid);
        setUserCVs(cvs);
        
        // Auto-select primary CV if available
        const primaryCV = await getUserPrimaryCV(currentUser.uid);
        if (primaryCV) {
          setSelectedCV(primaryCV);
        } else if (cvs.length > 0) {
          setSelectedCV(cvs[0]);
        }
        
        // Load screening profile
        const screening = await getUserScreeningProfile(currentUser.uid);
        setScreeningProfile(screening);
        
      } catch (error) {
        console.error('Error loading user data:', error);
        toast.error('Failed to load your profile data');
      } finally {
        setLoading(false);
      }
    };

    loadUserData();
  }, [currentUser]);

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setApplicationData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // Validate form data
  const validateForm = () => {
    const newErrors = {};
    
    if (!selectedCV) {
      newErrors.cv = 'Please select a CV to submit with your application';
    }
    
    if (!applicationData.coverLetter.trim()) {
      newErrors.coverLetter = 'Cover letter is required';
    } else if (applicationData.coverLetter.length < 100) {
      newErrors.coverLetter = 'Cover letter should be at least 100 characters';
    }
    
    if (applicationData.expectedSalary && isNaN(Number(applicationData.expectedSalary))) {
      newErrors.expectedSalary = 'Please enter a valid salary amount';
    }
    
    if (!applicationData.availabilityDate) {
      newErrors.availabilityDate = 'Please specify your availability date';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error('Please fix the errors before submitting');
      return;
    }
    
    try {
      setSubmitting(true);
      
      const application = {
        jobId: job.id,
        userId: currentUser.uid,
        cvId: selectedCV.id,
        screeningProfileId: screeningProfile?.id || null,
        coverLetter: applicationData.coverLetter,
        expectedSalary: applicationData.expectedSalary ? Number(applicationData.expectedSalary) : null,
        availabilityDate: applicationData.availabilityDate,
        additionalInfo: applicationData.additionalInfo,
        applicantInfo: {
          name: userProfile?.basicInfo?.name || '',
          email: userProfile?.basicInfo?.email || '',
          phone: userProfile?.basicInfo?.phone || '',
          university: userProfile?.academic?.university || '',
          degree: userProfile?.academic?.degree || ''
        }
      };
      
      await createJobApplication(application);
      
      toast.success('Application submitted successfully!');
      onSuccess();
      
    } catch (error) {
      console.error('Error submitting application:', error);
      toast.error(error.message || 'Failed to submit application');
    } finally {
      setSubmitting(false);
    }
  };

  // Navigate between steps
  const nextStep = () => {
    if (step === 1 && !selectedCV) {
      setErrors({ cv: 'Please select a CV to continue' });
      return;
    }
    setStep(prev => Math.min(prev + 1, 3));
  };

  const prevStep = () => {
    setStep(prev => Math.max(prev - 1, 1));
  };

  // Format salary display
  const formatSalary = (amount) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <div className="fixed inset-0 transition-opacity" onClick={onClose}>
            <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
          </div>
          <div className="inline-block align-middle bg-white rounded-lg px-6 py-8 text-center">
            <Loader className="w-8 h-8 animate-spin text-blue-500 mx-auto mb-4" />
            <p className="text-gray-600">Loading your profile...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div className="fixed inset-0 transition-opacity" onClick={onClose}>
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-t-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:rounded-lg">
          {/* Header */}
          <div className="bg-white px-6 pt-6 pb-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-gray-900">
                  Apply for {job.title}
                </h3>
                <p className="text-sm text-gray-500">{job.company.name}</p>
              </div>
              <button 
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500"
              >
                <X size={24} />
              </button>
            </div>

            {/* Progress indicator */}
            <div className="mt-4">
              <div className="flex items-center">
                {[1, 2, 3].map((stepNum) => (
                  <React.Fragment key={stepNum}>
                    <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
                      step >= stepNum 
                        ? 'bg-blue-600 text-white' 
                        : 'bg-gray-200 text-gray-600'
                    }`}>
                      {stepNum}
                    </div>
                    {stepNum < 3 && (
                      <div className={`flex-1 h-0.5 mx-2 ${
                        step > stepNum ? 'bg-blue-600' : 'bg-gray-200'
                      }`} />
                    )}
                  </React.Fragment>
                ))}
              </div>
              <div className="flex justify-between mt-2 text-xs text-gray-500">
                <span>Select CV</span>
                <span>Application Details</span>
                <span>Review & Submit</span>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="bg-white px-6 py-6 max-h-96 overflow-y-auto">
            {/* Step 1: CV Selection */}
            {step === 1 && (
              <div className="space-y-4">
                <h4 className="font-medium text-gray-900">Select CV to Submit</h4>
                
                {userCVs.length === 0 ? (
                  <div className="text-center py-8">
                    <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-600 mb-4">No CV found</p>
                    <p className="text-sm text-gray-500 mb-4">
                      You need to upload a CV before applying for jobs.
                    </p>
                    <button
                      onClick={() => window.open('/career/cv-manager', '_blank')}
                      className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                    >
                      Upload CV
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {userCVs.map((cv) => (
                      <div
                        key={cv.id}
                        className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                          selectedCV?.id === cv.id
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={() => setSelectedCV(cv)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <FileText className="w-5 h-5 text-gray-400 mr-3" />
                            <div>
                              <p className="font-medium text-gray-900">{cv.fileName}</p>
                              <p className="text-sm text-gray-500">
                                Uploaded {new Date(cv.uploadedAt.seconds * 1000).toLocaleDateString()}
                                {cv.isPrimary && <span className="ml-2 bg-green-100 text-green-800 px-2 py-0.5 rounded-full text-xs">Primary</span>}
                              </p>
                            </div>
                          </div>
                          {selectedCV?.id === cv.id && (
                            <CheckCircle className="w-5 h-5 text-blue-500" />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {errors.cv && (
                  <p className="text-red-600 text-sm flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {errors.cv}
                  </p>
                )}
              </div>
            )}

            {/* Step 2: Application Details */}
            {step === 2 && (
              <div className="space-y-4">
                <h4 className="font-medium text-gray-900">Application Details</h4>
                
                {/* Cover Letter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cover Letter *
                  </label>
                  <textarea
                    value={applicationData.coverLetter}
                    onChange={(e) => handleInputChange('coverLetter', e.target.value)}
                    placeholder="Explain why you're interested in this position and what makes you a great fit..."
                    rows={6}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.coverLetter ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.coverLetter && (
                    <p className="text-red-600 text-sm mt-1">{errors.coverLetter}</p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    {applicationData.coverLetter.length} characters (minimum 100)
                  </p>
                </div>

                {/* Expected Salary */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Expected Salary (ZAR per month)
                  </label>
                  <input
                    type="number"
                    value={applicationData.expectedSalary}
                    onChange={(e) => handleInputChange('expectedSalary', e.target.value)}
                    placeholder="e.g., 25000"
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.expectedSalary ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.expectedSalary && (
                    <p className="text-red-600 text-sm mt-1">{errors.expectedSalary}</p>
                  )}
                </div>

                {/* Availability Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Availability Date *
                  </label>
                  <input
                    type="date"
                    value={applicationData.availabilityDate}
                    onChange={(e) => handleInputChange('availabilityDate', e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.availabilityDate ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.availabilityDate && (
                    <p className="text-red-600 text-sm mt-1">{errors.availabilityDate}</p>
                  )}
                </div>

                {/* Additional Information */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Additional Information
                  </label>
                  <textarea
                    value={applicationData.additionalInfo}
                    onChange={(e) => handleInputChange('additionalInfo', e.target.value)}
                    placeholder="Any additional information you'd like to share..."
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            )}

            {/* Step 3: Review */}
            {step === 3 && (
              <div className="space-y-6">
                <h4 className="font-medium text-gray-900">Review Your Application</h4>
                
                {/* Job Summary */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h5 className="font-medium text-gray-900 mb-2">Applying for:</h5>
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0">
                      {job.company.logo ? (
                        <img 
                          src={job.company.logo} 
                          alt={job.company.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold">
                          {job.company.name.charAt(0)}
                        </div>
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{job.title}</p>
                      <p className="text-sm text-gray-600">{job.company.name}</p>
                      <p className="text-sm text-gray-500">{job.location}</p>
                    </div>
                  </div>
                </div>

                {/* Selected CV */}
                <div>
                  <h5 className="font-medium text-gray-900 mb-2">Selected CV:</h5>
                  <div className="flex items-center p-3 border border-gray-200 rounded-lg">
                    <FileText className="w-5 h-5 text-gray-400 mr-3" />
                    <span className="text-gray-700">{selectedCV?.fileName}</span>
                  </div>
                </div>

                {/* Application Summary */}
                <div>
                  <h5 className="font-medium text-gray-900 mb-3">Application Summary:</h5>
                  <div className="space-y-3 text-sm">
                    <div>
                      <span className="font-medium text-gray-600">Cover Letter:</span>
                      <p className="mt-1 text-gray-700 bg-gray-50 p-3 rounded max-h-24 overflow-y-auto">
                        {applicationData.coverLetter}
                      </p>
                    </div>
                    
                    {applicationData.expectedSalary && (
                      <div>
                        <span className="font-medium text-gray-600">Expected Salary:</span>
                        <span className="ml-2 text-gray-700">
                          {formatSalary(applicationData.expectedSalary)} per month
                        </span>
                      </div>
                    )}
                    
                    <div>
                      <span className="font-medium text-gray-600">Available from:</span>
                      <span className="ml-2 text-gray-700">
                        {new Date(applicationData.availabilityDate).toLocaleDateString()}
                      </span>
                    </div>
                    
                    {applicationData.additionalInfo && (
                      <div>
                        <span className="font-medium text-gray-600">Additional Information:</span>
                        <p className="mt-1 text-gray-700">{applicationData.additionalInfo}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Screening Profile Status */}
                {screeningProfile ? (
                  <div className="bg-green-50 p-4 rounded-lg">
                    <div className="flex items-center">
                      <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                      <span className="text-green-800 font-medium">
                        Audio screening profile completed
                      </span>
                    </div>
                    <p className="text-green-700 text-sm mt-1">
                      Your audio responses will be included with this application
                    </p>
                  </div>
                ) : (
                  <div className="bg-yellow-50 p-4 rounded-lg">
                    <div className="flex items-center">
                      <AlertCircle className="w-5 h-5 text-yellow-500 mr-2" />
                      <span className="text-yellow-800 font-medium">
                        No audio screening profile
                      </span>
                    </div>
                    <p className="text-yellow-700 text-sm mt-1">
                      Consider completing your audio screening to strengthen your application
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 flex justify-between">
            <div>
              {step > 1 && (
                <button
                  onClick={prevStep}
                  className="px-4 py-2 text-gray-600 hover:text-gray-900 font-medium"
                >
                  Previous
                </button>
              )}
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-600 hover:text-gray-900 font-medium"
              >
                Cancel
              </button>
              
              {step < 3 ? (
                <button
                  onClick={nextStep}
                  disabled={step === 1 && userCVs.length === 0}
                  className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                    step === 1 && userCVs.length === 0
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  Next
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                    submitting
                      ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                      : 'bg-green-600 text-white hover:bg-green-700'
                  }`}
                >
                  {submitting ? (
                    <>
                      <Loader className="w-4 h-4 mr-2 animate-spin inline" />
                      Submitting...
                    </>
                  ) : (
                    'Submit Application'
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApplicationModal;